/**
 * PredictiveSearch Custom Element
 * Handles live predictive search fetching from /search/suggest.json
 * and manages search modal keyboard navigation and focus.
 */

class PredictiveSearch extends HTMLElement {
  #input = null;
  #resultsContainer = null;
  #abortController = null;
  #debounceTimer = null;

  constructor() {
    super();
  }

  connectedCallback() {
    this.#input = this.querySelector('input[type="search"]');
    this.#resultsContainer = this.querySelector('[data-predictive-search-results]');

    if (this.#input) {
      this.#input.addEventListener('input', this.#onInput.bind(this));
      this.#input.addEventListener('focus', this.#onFocus.bind(this));
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.classList.contains('is-open')) {
        this.close();
      }
    });

    window.addEventListener('predictive-search:open', () => this.open());
    window.addEventListener('predictive-search:close', () => this.close());
  }

  open() {
    this.classList.add('is-open');
    this.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    setTimeout(() => {
      if (this.#input) this.#input.focus();
    }, 100);
  }

  close() {
    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    if (this.#resultsContainer) this.#resultsContainer.innerHTML = '';
    if (this.#input) this.#input.value = '';
  }

  #onInput() {
    const query = this.#input.value.trim();

    if (this.#debounceTimer) clearTimeout(this.#debounceTimer);

    if (!query.length) {
      if (this.#resultsContainer) this.#resultsContainer.innerHTML = '';
      return;
    }

    this.#debounceTimer = setTimeout(() => {
      this.#fetchResults(query);
    }, 200);
  }

  #onFocus() {
    const query = this.#input.value.trim();
    if (query.length && this.#resultsContainer && !this.#resultsContainer.innerHTML) {
      this.#fetchResults(query);
    }
  }

  #fetchResults(query) {
    if (this.#abortController) this.#abortController.abort();
    this.#abortController = new AbortController();

    const url = `${window.Shopify?.routes?.root || '/'}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6`;

    fetch(url, { signal: this.#abortController.signal })
      .then((res) => {
        if (!res.ok) throw new Error('Search failed');
        return res.json();
      })
      .then((data) => {
        const products = data.resources?.results?.products || [];
        this.#renderResults(products, query);
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          console.error('Predictive search error:', err);
        }
      });
  }

  #renderResults(products, query) {
    if (!this.#resultsContainer) return;

    if (products.length === 0) {
      this.#resultsContainer.innerHTML = `
        <div class="search-modal__empty">
          <p>No products found for "<strong>${this.#escapeHtml(query)}</strong>"</p>
        </div>
      `;
      return;
    }

    const html = `
      <div class="search-modal__results-heading">Matching Products</div>
      <ul class="search-modal__products-list" role="list">
        ${products
          .map(
            (product) => `
          <li class="search-modal__product-item">
            <a href="${product.url}" class="search-modal__product-link">
              ${
                product.image
                  ? `<img src="${product.image}" alt="${this.#escapeHtml(product.title)}" class="search-modal__product-img" width="50" height="50" loading="lazy">`
                  : `<div class="search-modal__product-placeholder"></div>`
              }
              <div class="search-modal__product-details">
                <span class="search-modal__product-title">${this.#escapeHtml(product.title)}</span>
                <span class="search-modal__product-price">$${(product.price / 100).toFixed(2)}</span>
              </div>
            </a>
          </li>
        `
          )
          .join('')}
      </ul>
      <div class="search-modal__footer">
        <button type="submit" class="btn btn--primary btn--sm" form="HeaderSearchForm">
          View all results for "${this.#escapeHtml(query)}"
        </button>
      </div>
    `;

    this.#resultsContainer.innerHTML = html;
  }

  #escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, (match) => {
      const escape = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;'
      };
      return escape[match];
    });
  }
}

customElements.define('predictive-search', PredictiveSearch);
